import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ChevronDown,
  FolderOpen,
  FolderClosed,
  FileText,
  HardDrive,
  Loader2,
  AlertCircle,
  File,
  FileSpreadsheet,
  FileImage,
  Search,
} from "lucide-react";
import { api } from "@/api/client";
import type {
  DocumentTreeResponse,
  DocumentTreeJenis,
  DocumentTreeSiklus,
  DocumentTreeBentuk,
  DocumentTreeFile,
  DocumentStatus,
  ConfidentialityLevel,
} from "@/types/document";
import {
  STATUS_COLORS,
  CONFIDENTIALITY_COLORS,
  CONFIDENTIALITY_LABELS,
} from "@/types/document";

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatFileSize(bytes?: number): string {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function getFileIcon(mimeType?: string) {
  if (!mimeType) return File;
  if (mimeType.includes("pdf")) return FileText;
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel")) return FileSpreadsheet;
  if (mimeType.includes("image")) return FileImage;
  return File;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// ── Tree Node Components ─────────────────────────────────────────────────────

function FileNode({
  doc,
  onClick,
}: {
  doc: DocumentTreeFile;
  onClick: () => void;
}) {
  const Icon = getFileIcon(doc.mime_type);

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2.5 py-2 px-3 rounded-lg hover:bg-amber-50 transition-colors group text-left"
    >
      <Icon className="h-4 w-4 flex-shrink-0 text-amber-600" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 truncate">
            {doc.judul}
          </span>
          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[doc.status_kode]}`}>
            {doc.status_nama}
          </span>
          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${CONFIDENTIALITY_COLORS[doc.tingkat_kerahasiaan]}`}>
            {CONFIDENTIALITY_LABELS[doc.tingkat_kerahasiaan]}
          </span>
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          {doc.file_name && (
            <span className="text-xs text-gray-400 font-mono truncate max-w-[200px]">
              {doc.file_name}
            </span>
          )}
          <span className="text-xs text-gray-400">
            {formatFileSize(doc.file_size)}
          </span>
          <span className="text-xs text-gray-400">
            v{doc.versi_terakhir}
          </span>
          <span className="text-xs text-gray-400">
            {doc.created_by_username}
          </span>
          <span className="text-xs text-gray-400">
            {formatDate(doc.created_at)}
          </span>
        </div>
      </div>
      <ChevronRight className="h-3.5 w-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    </button>
  );
}

function BentukNode({
  bentuk,
  searchQuery,
  onDocClick,
}: {
  bentuk: DocumentTreeBentuk;
  searchQuery: string;
  onDocClick: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(searchQuery.length > 0);
  const Chevron = expanded ? ChevronDown : ChevronRight;
  const Folder = expanded ? FolderOpen : FolderClosed;

  const filteredDocs = searchQuery
    ? bentuk.documents.filter(
        (d) =>
          d.judul.toLowerCase().includes(searchQuery) ||
          d.file_name?.toLowerCase().includes(searchQuery) ||
          d.created_by_username.toLowerCase().includes(searchQuery),
      )
    : bentuk.documents;

  if (searchQuery && filteredDocs.length === 0) return null;

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-purple-50 transition-colors group"
      >
        <Chevron className="h-3.5 w-3.5 text-purple-400 flex-shrink-0" />
        <Folder className="h-4 w-4 text-purple-600 flex-shrink-0" />
        <span className="font-mono text-xs font-semibold text-purple-700">{bentuk.kode}</span>
        <span className="text-sm text-gray-700">{bentuk.nama}</span>
        <span className="ml-auto rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
          {filteredDocs.length}
        </span>
      </button>
      {expanded && filteredDocs.length > 0 && (
        <div className="ml-6 pl-3 border-l-2 border-purple-100">
          {filteredDocs.map((doc) => (
            <FileNode key={doc.id} doc={doc} onClick={() => onDocClick(doc.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function SiklusNode({
  siklus,
  searchQuery,
  onDocClick,
}: {
  siklus: DocumentTreeSiklus;
  searchQuery: string;
  onDocClick: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(searchQuery.length > 0);
  const Chevron = expanded ? ChevronDown : ChevronRight;
  const Folder = expanded ? FolderOpen : FolderClosed;

  const filteredSiklusCount = searchQuery
    ? siklus.bentuk_list.reduce(
        (acc, b) =>
          acc +
          b.documents.filter(
            (d) =>
              d.judul.toLowerCase().includes(searchQuery) ||
              d.file_name?.toLowerCase().includes(searchQuery) ||
              d.created_by_username.toLowerCase().includes(searchQuery),
          ).length,
        0,
      )
    : siklus.total;

  if (searchQuery && filteredSiklusCount === 0) return null;

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-emerald-50 transition-colors group"
      >
        <Chevron className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />
        <Folder className="h-4 w-4 text-emerald-600 flex-shrink-0" />
        <span className="font-mono text-xs font-semibold text-emerald-700">{siklus.kode}</span>
        <span className="text-sm text-gray-700">{siklus.nama}</span>
        <span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
          {filteredSiklusCount}
        </span>
      </button>
      {expanded && (
        <div className="ml-6 pl-3 border-l-2 border-emerald-100">
          {siklus.bentuk_list.map((bentuk) => (
            <BentukNode
              key={bentuk.kode}
              bentuk={bentuk}
              searchQuery={searchQuery}
              onDocClick={onDocClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function JenisNode({
  jenis,
  searchQuery,
  onDocClick,
}: {
  jenis: DocumentTreeJenis;
  searchQuery: string;
  onDocClick: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(searchQuery.length > 0);
  const Chevron = expanded ? ChevronDown : ChevronRight;
  const Folder = expanded ? FolderOpen : FolderClosed;

  const filteredJenisCount = searchQuery
    ? jenis.siklus_list.reduce(
        (acc, s) =>
          acc +
          s.bentuk_list.reduce(
            (acc2, b) =>
              acc2 +
              b.documents.filter(
                (d) =>
                  d.judul.toLowerCase().includes(searchQuery) ||
                  d.file_name?.toLowerCase().includes(searchQuery) ||
                  d.created_by_username.toLowerCase().includes(searchQuery),
              ).length,
            0,
          ),
        0,
      )
    : jenis.total;

  if (searchQuery && filteredJenisCount === 0) return null;

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 py-2.5 px-3 rounded-lg hover:bg-blue-50 transition-colors group"
      >
        <Chevron className="h-4 w-4 text-blue-400 flex-shrink-0" />
        <Folder className="h-5 w-5 text-blue-600 flex-shrink-0" />
        <span className="font-mono text-sm font-bold text-blue-700">{jenis.kode}</span>
        <span className="text-sm font-medium text-gray-800">{jenis.nama}</span>
        <span className="ml-auto rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
          {filteredJenisCount}
        </span>
      </button>
      {expanded && (
        <div className="ml-6 pl-3 border-l-2 border-blue-100">
          {jenis.siklus_list.map((siklus) => (
            <SiklusNode
              key={siklus.kode}
              siklus={siklus}
              searchQuery={searchQuery}
              onDocClick={onDocClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function DocumentTreePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [allExpanded, setAllExpanded] = useState(false);

  const { data, isLoading, isError } = useQuery<DocumentTreeResponse>({
    queryKey: ["documents", "tree"],
    queryFn: () => api.get("/documents/tree"),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        <span className="ml-2 text-sm text-gray-500">Memuat struktur dokumen...</span>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
        <AlertCircle className="h-5 w-5 flex-shrink-0" />
        <span className="text-sm">Gagal memuat struktur dokumen.</span>
      </div>
    );
  }

  const normalizedQuery = searchQuery.toLowerCase().trim();

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Struktur Dokumen</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Tampilan tree directory dokumen berdasarkan taksonomi penyimpanan
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Total Dokumen</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{data.total_documents}</p>
        </div>
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <p className="text-xs text-blue-600 uppercase tracking-wide">Jenis PNBP</p>
          <p className="text-xl font-bold text-blue-800 mt-1">{data.total_jenis}</p>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-xs text-emerald-600 uppercase tracking-wide">Siklus Keuangan</p>
          <p className="text-xl font-bold text-emerald-800 mt-1">{data.total_siklus}</p>
        </div>
        <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
          <p className="text-xs text-purple-600 uppercase tracking-wide">Bentuk Dokumen</p>
          <p className="text-xl font-bold text-purple-800 mt-1">{data.total_bentuk}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cari dokumen dalam tree..."
            className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={() => setAllExpanded(!allExpanded)}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          {allExpanded ? "Tutup Semua" : "Buka Semua"}
        </button>
      </div>

      {/* Tree */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {/* Root node */}
        <div className="flex items-center gap-2.5 px-4 py-3 bg-gray-50 border-b border-gray-200">
          <HardDrive className="h-5 w-5 text-gray-600" />
          <span className="text-sm font-bold text-gray-700">pnbp/</span>
          <span className="text-xs text-gray-400">PNBP Document Storage</span>
          <span className="ml-auto text-xs text-gray-500">
            {data.total_documents} dokumen
          </span>
        </div>

        <div className="p-3">
          {data.tree.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-400">
              Belum ada dokumen yang tersimpan.
            </div>
          ) : (
            <div key={allExpanded ? "expanded" : "collapsed"}>
              {data.tree.map((jenis) => (
                <JenisNode
                  key={jenis.kode}
                  jenis={jenis}
                  searchQuery={normalizedQuery}
                  onDocClick={(id) => navigate(`/documents/${id}`)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 px-4 py-2.5 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <FolderOpen className="h-3 w-3 text-blue-600" /> Jenis PNBP
          </span>
          <span className="flex items-center gap-1">
            <FolderOpen className="h-3 w-3 text-emerald-600" /> Siklus
          </span>
          <span className="flex items-center gap-1">
            <FolderOpen className="h-3 w-3 text-purple-600" /> Bentuk
          </span>
          <span className="flex items-center gap-1">
            <FileText className="h-3 w-3 text-amber-600" /> Dokumen
          </span>
        </div>
      </div>
    </div>
  );
}
